import React from "react";
import { FiClock, FiTag } from "react-icons/fi";
import { PackageConfig, Addon } from "@/types";
import { formatCurrency } from "@/utils/format";
import Badge from "@/components/ui/Badge";
import SavingsCalculator from "./SavingsCalculator";

interface EnhancedPackageCardProps {
  package: PackageConfig;
  includedAddons: Addon[];
  isSelected: boolean;
  onSelect: () => void;
  className?: string;
}

const EnhancedPackageCard: React.FC<EnhancedPackageCardProps> = ({
  package: pkg,
  includedAddons,
  isSelected,
  onSelect,
  className = "",
}) => {
  const isLimitedTime = pkg.validUntil && new Date(pkg.validUntil) > new Date();
  const daysRemaining = pkg.validUntil
    ? Math.ceil((new Date(pkg.validUntil).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div
      onClick={onSelect}
      className={`relative p-4 sm:p-5 border-2 rounded-lg cursor-pointer transition-all ${
        isSelected
          ? "border-primary-600 bg-primary-50 dark:bg-primary-900/20 shadow-lg scale-[1.02]"
          : "border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:shadow-md"
      } ${className}`}
    >
      {/* Selected checkmark — absolute top-right corner */}
      {isSelected && (
        <span
          className="absolute top-3 right-3 inline-flex items-center justify-center bg-primary-600 text-white rounded-full p-1"
          aria-label="Selected"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}

      {/* Badge */}
      {pkg.badge && (
        <div className="absolute -top-3 left-4">
          <Badge
            variant={
              pkg.badge === "popular" || pkg.badge === "best-value"
                ? "primary"
                : pkg.badge === "recommended"
                  ? "success"
                  : pkg.badge === "limited-time"
                    ? "warning"
                    : pkg.badge === "seasonal"
                      ? "info"
                      : "default"
            }
            size="md"
          >
            {pkg.badgeText || pkg.badge.toUpperCase()}
          </Badge>
        </div>
      )}

      <div className="flex justify-between items-start mb-3 mt-2">
        <div className="flex-1">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            {pkg.name}
          </h3>
          {pkg.isSubscription && pkg.billingInterval && (
            <span className="inline-block mt-1 text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 rounded-full">
              🔄 {pkg.billingInterval}
            </span>
          )}
          {pkg.seasonal && pkg.seasonalTheme && (
            <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
              🎉 {pkg.seasonalTheme}
            </p>
          )}
        </div>
        <div className={`text-right ml-4 ${isSelected ? "pr-8" : ""}`}>
          {pkg.originalPrice && pkg.originalPrice > pkg.price && (
            <div className="text-sm text-gray-500 dark:text-gray-400 line-through">
              {formatCurrency(pkg.originalPrice)}
            </div>
          )}
          <div className="text-xl sm:text-2xl font-bold text-primary-600">
            {formatCurrency(pkg.price)}
            {pkg.isSubscription && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mo</span>
            )}
          </div>
        </div>
      </div>

      {/* Savings Calculator */}
      {pkg.originalPrice && pkg.originalPrice > pkg.price && (
        <div className="mb-3">
          <SavingsCalculator
            originalPrice={pkg.originalPrice}
            currentPrice={pkg.price}
            discount={pkg.discount}
          />
        </div>
      )}

      {/* Limited Time Offer Notice */}
      {isLimitedTime && (
        <div className="mb-3 flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-3 py-2 rounded-lg">
          <FiClock size={16} />
          <span className="font-medium">
            {daysRemaining > 0
              ? `Only ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} left!`
              : "Ending soon!"}
          </span>
        </div>
      )}

      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{pkg.details}</p>

      {/* Included Features/Addons */}
      {includedAddons.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Includes:</span>
          <div className="flex flex-wrap gap-1 mt-2">
            {includedAddons.map((addon) => (
              <Badge key={addon.id} variant="success" size="sm">
                {addon.title}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Bundle Deal Notice */}
      {pkg.discount && pkg.discount > 0 && !pkg.originalPrice && (
        <div className="mt-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
          <FiTag size={16} />
          <span className="font-medium">Bundle Deal: {pkg.discount}% OFF</span>
        </div>
      )}

      {/* Selection Indicator is shown inline near the price (mobile-safe) */}
    </div>
  );
};

export default EnhancedPackageCard;
